import IntranetLayout from "@/components/IntranetLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Ship, Users, Shield, FileText, HelpCircle, BarChart3,
  Megaphone, BookOpen, GraduationCap, Scale, AlertTriangle, Handshake,
  ArrowRight, TrendingUp, Clock, CheckCircle2, Target, Settings,
  Anchor, Wrench, DollarSign, Star, Leaf, ChevronRight, Gauge, MapPin
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useMemo, useRef, useState, useEffect } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MOCK_VESSELS, MOCK_CERTIFICATES, MOCK_VOYAGES, PLATFORM_KPIS,
  MOCK_SATISFACTION_DATA, MOCK_EMISSIONS, VESSEL_STATUS_COLORS,
  OFFSHORE_ASSETS, CLIENT_DISPLAY_NAMES,
} from "@/data/portalMockData";

const REPORT_USERS = ["jerome", "kyle", "bo", "eduard"];

// ── Vessel Map (full fleet + offshore assets) ────────────────
const VesselMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let map: any;
    const loadMap = async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (!mapRef.current) return;

      map = L.map(mapRef.current, {
        center: [0, -20], zoom: 2,
        zoomControl: false, attributionControl: false,
      });

      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      // All fleet vessels
      MOCK_VESSELS.forEach((v) => {
        const statusInfo = VESSEL_STATUS_COLORS[v.status] || VESSEL_STATUS_COLORS.available;
        const color = statusInfo.bg;
        const isMoving = v.status === "in_transit";
        const pulseRing = isMoving ? `<div style="position:absolute;top:-4px;left:-4px;width:20px;height:20px;border-radius:50%;border:2px solid ${color};opacity:0.4;animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>` : "";
        const icon = L.divIcon({
          className: "",
          html: `<div style="position:relative;width:14px;height:14px;">${pulseRing}<div style="position:absolute;top:1px;left:1px;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 8px ${color}80;"></div></div>`,
          iconSize: [14, 14], iconAnchor: [7, 7],
        });
        const statusLabel = statusInfo.label;
        const clientDisplay = CLIENT_DISPLAY_NAMES[v.client] || v.client;
        const alongsideInfo = v.status === "alongside" ? `<div style="font-size:11px;color:#28A745;font-weight:600;margin:4px 0;">⚓ Alongside ${v.destination || ""}</div>` : "";
        const transitInfo = v.status === "in_transit" ? `<div style="font-size:11px;color:#0066CC;font-weight:600;margin:4px 0;">🚢 En route to ${v.destination || ""}</div>` : "";
        const shoreInfo = v.status === "available" ? `<div style="font-size:11px;color:#17A2B8;font-weight:600;margin:4px 0;">🏠 At Shore Base</div>` : "";
        const paxLine = v.paxOnBoard > 0
          ? `<div style="font-size:11px;margin:2px 0;">👥 <b>${v.paxOnBoard}</b> passengers on board</div>`
          : `<div style="font-size:11px;color:#888;margin:2px 0;">No passengers on board</div>`;
        L.marker([v.lat, v.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:240px;max-width:300px;">
              <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${v.name}</div>
              <div style="font-size:11px;color:#666;margin-bottom:6px;">${v.type}</div>
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};"></span>
                <span style="font-size:12px;font-weight:600;">${statusLabel}</span>
              </div>
              ${alongsideInfo}${transitInfo}${shoreInfo}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:11px;margin:2px 0;">📋 <b>Client:</b> ${clientDisplay}</div>
              <div style="font-size:11px;margin:2px 0;">🏴 <b>Flag:</b> ${v.flag}</div>
              <div style="font-size:11px;margin:2px 0;">⚡ <b>Speed:</b> ${v.currentSpeed > 0 ? v.currentSpeed + " kts" : "0 kts (stationary)"}</div>
              <div style="font-size:11px;margin:2px 0;">👨‍✈️ <b>Captain:</b> ${v.captain}</div>
              <div style="font-size:11px;margin:2px 0;">🔧 <b>Chief Engineer:</b> ${v.chiefEngineer}</div>
              ${paxLine}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:10px;color:#aaa;">IMO: ${v.imoNumber} · MMSI: ${v.mmsi}</div>
              <div style="font-size:10px;color:#aaa;">${v.lat.toFixed(2)}°${v.lat >= 0 ? "N" : "S"}, ${Math.abs(v.lng).toFixed(2)}°${v.lng >= 0 ? "E" : "W"}</div>
            </div>
          `, { maxWidth: 320 });
      });

      const assetColors: Record<string, string> = { FPSO: "#E74C3C", Drillship: "#3498DB", Terminal: "#FF9F1C", "Semi-Sub": "#F39C12" };
      OFFSHORE_ASSETS.forEach((asset) => {
        const color = assetColors[asset.type] || "#fff";
        const opStatus = asset.operatorStatus || "operated";
        const size = asset.type === "Terminal" ? 18 : 14;
        let shape: string;
        if (asset.type === "Terminal") {
          const opacity = opStatus === "future" ? "0.4" : opStatus === "non-operated" ? "0.6" : "1";
          shape = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" style="filter:drop-shadow(0 0 4px ${color}80);opacity:${opacity};"><path d="M12 2L12 10M12 10C9.79 10 8 11.79 8 14M12 10C14.21 10 16 11.79 16 14M8 14H5L7 22H17L19 14H16M8 14H16M6 6H18" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        } else if (opStatus === "future") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:transparent;border:2px dashed ${color};box-shadow:0 0 10px ${color}40;${tr}"></div>`;
        } else if (opStatus === "non-operated") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:${color}55;border:2px dashed #fff;box-shadow:0 0 10px ${color}60;${tr}"></div>`;
        } else {
          shape = asset.type === "FPSO"
            ? `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;box-shadow:0 0 10px ${color}80;transform:rotate(45deg);"></div>`
            : `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 10px ${color}80;"></div>`;
        }
        const icon = L.divIcon({ className: "", html: shape, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
        const clientDisplay = CLIENT_DISPLAY_NAMES[asset.client] || asset.client;
        L.marker([asset.lat, asset.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:200px;">
              <div style="font-weight:700;font-size:14px;">${asset.name}</div>
              <div style="font-size:11px;color:#888;">${asset.type} · ${asset.region}</div>
              <div style="font-size:11px;color:#888;">Client: ${clientDisplay}</div>
              ${asset.field ? `<div style="font-size:11px;color:#aaa;">Field: ${asset.field}</div>` : ""}
            </div>
          `);
      });

      // Fit bounds to all points
      const allPoints = [
        ...MOCK_VESSELS.map((v) => [v.lat, v.lng] as [number, number]),
        ...OFFSHORE_ASSETS.map((a) => [a.lat, a.lng] as [number, number]),
      ];
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
      setMapReady(true);
    };
    loadMap();
    return () => { map?.remove(); };
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden border border-border" style={{ height: 380 }}>
      {!mapReady && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
};

// ── Permission-aware module config ───────────────────────────
const MODULE_WIDGETS = [
  { permKey: "page_fleet", label: "Fleet & Vessels", href: "/employee/fleet", icon: Ship, color: "#0ea5e9" },
  { permKey: "page_fleet", label: "Voyage Operations", href: "/employee/voyage-operations", icon: Anchor, color: "#0066CC" },
  { permKey: "page_crew_roster", label: "Crew & Personnel", href: "/employee/crew-personnel", icon: Users, color: "#8b5cf6" },
  { permKey: "page_safety", label: "Safety & HSE", href: "/employee/safety", icon: Shield, color: "#DC3545" },
  { permKey: "page_documents", label: "Documents & SOPs", href: "/employee/documents", icon: FileText, color: "#10b981" },
  { permKey: "page_commercial", label: "Commercial & Finance", href: "/employee/commercial-finance", icon: DollarSign, color: "#f59e0b" },
  { permKey: "page_contracts", label: "Contracts", href: "/employee/contracts", icon: Handshake, color: "#06b6d4" },
  { permKey: "page_supply_chain", label: "Supply Chain", href: "/employee/supply-chain", icon: TrendingUp, color: "#84cc16" },
  { permKey: "page_fleet", label: "Maintenance & PMS", href: "/employee/maintenance", icon: Wrench, color: "#ef4444" },
  { permKey: "page_compliance", label: "Compliance Calendar", href: "/employee/compliance", icon: Scale, color: "#a855f7" },
  { permKey: "page_training", label: "Training & Certs", href: "/employee/training", icon: GraduationCap, color: "#14b8a6" },
  { permKey: "page_commercial", label: "ESG & Emissions", href: "/employee/esg-emissions", icon: Leaf, color: "#22c55e" },
  { permKey: "page_commercial", label: "Reports & Analytics", href: "/employee/reports-analytics", icon: BarChart3, color: "#6366f1" },
  { permKey: "page_commercial", label: "Client Satisfaction", href: "/employee/satisfaction", icon: Star, color: "#eab308" },
  { permKey: "page_questions", label: "Client Questions", href: "/employee/questions", icon: HelpCircle, color: "#f97316" },
  { permKey: "page_progress", label: "Project Updates", href: "/employee/progress", icon: BarChart3, color: "#0284c7" },
  { permKey: "page_announcements", label: "Announcements", href: "/employee/announcements", icon: Megaphone, color: "#ec4899" },
  { permKey: "page_emergency_contacts", label: "Emergency Contacts", href: "/employee/emergency-contacts", icon: AlertTriangle, color: "#f43f5e" },
  { permKey: "page_vendors", label: "Vendor Directory", href: "/employee/vendors", icon: Handshake, color: "#0d9488" },
  { permKey: "page_onboarding", label: "Onboarding", href: "/employee/onboarding", icon: BookOpen, color: "#7c3aed" },
  { permKey: "page_directory", label: "Org Chart", href: "/employee/directory", icon: Users, color: "#64748b" },
];

const CHART_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];

const EmployeeDashboard = () => {
  const { fullName, user, employeePermissions, isAdmin } = useAuth();

  // Helper: check if a module is accessible
  const hasAccess = (permKey: string) => {
    if (isAdmin) return true;
    return (employeePermissions as any)?.[permKey] ?? false;
  };

  const accessibleModules = MODULE_WIDGETS.filter(m => hasAccess(m.permKey));

  // ── Data queries ─────────────────────────────────────────
  const { data: avatarUrl } = useQuery({
    queryKey: ["emp-avatar", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("employee_directory").select("avatar_url")
        .eq("user_id", user!.id).maybeSingle();
      return data?.avatar_url || null;
    },
    enabled: !!user?.id,
  });

  const initials = (fullName || user?.email || "U").charAt(0).toUpperCase();

  const { data: pendingQuestions = 0 } = useQuery({
    queryKey: ["emp-pending-q"],
    queryFn: async () => {
      const { count } = await supabase.from("technical_questions").select("*", { count: "exact", head: true }).eq("status", "pending");
      return count ?? 0;
    },
  });

  const { data: docCount = 0 } = useQuery({
    queryKey: ["emp-doc-count"],
    queryFn: async () => {
      const { count } = await supabase.from("documents").select("*", { count: "exact", head: true });
      return count ?? 0;
    },
  });

  const { data: updateCount = 0 } = useQuery({
    queryKey: ["emp-update-count"],
    queryFn: async () => {
      const { count } = await supabase.from("project_updates").select("*", { count: "exact", head: true });
      return count ?? 0;
    },
  });

  const { data: announcementCount = 0 } = useQuery({
    queryKey: ["emp-announcement-count"],
    queryFn: async () => {
      const { count } = await supabase.from("announcements").select("*", { count: "exact", head: true });
      return count ?? 0;
    },
  });

  const { data: recentAnnouncements = [] } = useQuery({
    queryKey: ["emp-recent-announcements"],
    queryFn: async () => {
      const { data } = await supabase.from("announcements").select("*").order("created_at", { ascending: false }).limit(3);
      return data ?? [];
    },
  });

  const { data: recentQuestions = [] } = useQuery({
    queryKey: ["emp-recent-questions"],
    queryFn: async () => {
      const { data } = await supabase.from("technical_questions").select("*, companies(name)").eq("status", "pending").order("created_at", { ascending: false }).limit(5);
      return data ?? [];
    },
  });

  // Vessels & crew
  const { data: vessels = [], isLoading: loadingVessels } = useQuery({
    queryKey: ["dash-fleet-vessels"],
    queryFn: async () => {
      const { data } = await supabase.from("fleet_vessels").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: crewSchedules = [] } = useQuery({
    queryKey: ["dash-crew-schedules"],
    queryFn: async () => {
      const { data } = await supabase.from("crew_schedules").select("*");
      return data ?? [];
    },
  });

  // Satisfaction surveys
  const { data: liveSurveys = [] } = useQuery({
    queryKey: ["dash-satisfaction-surveys"],
    queryFn: async () => {
      const { data } = await supabase.from("satisfaction_surveys").select("*").order("submitted_at", { ascending: false }).limit(100);
      return (data ?? []) as any[];
    },
  });

  // Contracts
  const { data: contracts = [] } = useQuery({
    queryKey: ["dash-contracts"],
    queryFn: async () => {
      const { data } = await supabase.from("contracts").select("*");
      return data ?? [];
    },
  });

  // Compliance data
  const { data: smsReviewDocs = [] } = useQuery({
    queryKey: ["dash-sms-docs"],
    queryFn: async () => {
      const { data } = await supabase.from("operating_documents").select("id").eq("is_sms_review", true).eq("is_archived", false);
      return data ?? [];
    },
  });

  const { data: readConfirmations = [] } = useQuery({
    queryKey: ["dash-read-confirmations"],
    queryFn: async () => {
      const { data } = await supabase.from("document_read_confirmations").select("document_id, user_id, user_name");
      return data ?? [];
    },
  });

  const { data: allProfiles = [] } = useQuery({
    queryKey: ["dash-profiles-compliance"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("user_id, full_name");
      return data ?? [];
    },
  });

  // ── Computed data ────────────────────────────────────────
  const statusCounts = useMemo(() => ({
    on_charter: MOCK_VESSELS.filter((v) => ["on_charter", "in_transit", "alongside"].includes(v.status)).length,
    standby: MOCK_VESSELS.filter((v) => v.status === "standby").length,
    maintenance: MOCK_VESSELS.filter((v) => v.status === "maintenance").length,
    available: MOCK_VESSELS.filter((v) => v.status === "available").length,
  }), []);

  const onboardCrew = crewSchedules.filter((c: any) => c.status === "onboard").length;
  const activeContracts = contracts.filter((c: any) => c.status === "active").length;

  const liveOverallScore = useMemo(() => {
    if (!liveSurveys.length) return MOCK_SATISFACTION_DATA.overallScore;
    return Math.round((liveSurveys.reduce((s: number, r: any) => s + (r.rating_overall || 0), 0) / liveSurveys.length) * 10) / 10;
  }, [liveSurveys]);

  const liveTrends = useMemo(() => {
    if (!liveSurveys.length) return MOCK_SATISFACTION_DATA.trends;
    const monthMap: Record<string, { overall: number[]; crew: number[]; punctuality: number[] }> = {};
    liveSurveys.forEach((s: any) => {
      const d = new Date(s.submitted_at);
      const key = d.toLocaleString("en", { month: "short" });
      if (!monthMap[key]) monthMap[key] = { overall: [], crew: [], punctuality: [] };
      if (s.rating_overall) monthMap[key].overall.push(s.rating_overall);
      if (s.rating_crew) monthMap[key].crew.push(s.rating_crew);
      if (s.rating_punctuality) monthMap[key].punctuality.push(s.rating_punctuality);
    });
    const avg = (arr: number[]) => arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10 : null;
    return Object.entries(monthMap).map(([month, d]) => ({
      month, overall: avg(d.overall), crew: avg(d.crew), punctuality: avg(d.punctuality),
    }));
  }, [liveSurveys]);

  const urgentCerts = MOCK_CERTIFICATES
    .filter((c) => c.daysRemaining <= 90)
    .sort((a, b) => a.daysRemaining - b.daysRemaining);

  const recentVoyages = MOCK_VOYAGES
    .filter((v) => v.status === "completed" || v.status === "in_progress")
    .slice(0, 5);

  const emissionsData = MOCK_EMISSIONS.monthly.slice(-3);

  // Compliance completion
  const complianceData = useMemo(() => {
    const totalDocs = smsReviewDocs.length;
    if (!totalDocs || !allProfiles.length) return [];
    const smsDocIds = new Set(smsReviewDocs.map((d: any) => d.id));
    const trackedProfiles = allProfiles.filter((p: any) => {
      if (!p.full_name) return false;
      const nameLower = p.full_name.toLowerCase();
      if (user?.id && p.user_id === user.id) return true;
      return REPORT_USERS.some(n => nameLower.includes(n));
    });
    const seen = new Set<string>();
    const unique = trackedProfiles.filter((p: any) => {
      if (seen.has(p.user_id)) return false;
      seen.add(p.user_id);
      return true;
    });
    return unique.map((p: any) => {
      const readCount = readConfirmations.filter(
        (rc: any) => rc.user_id === p.user_id && smsDocIds.has(rc.document_id)
      ).length;
      const pct = totalDocs > 0 ? Math.round((readCount / totalDocs) * 100) : 0;
      return { name: p.full_name, readCount, totalDocs, pct };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [smsReviewDocs, readConfirmations, allProfiles, user?.id]);

  // Vessel utilization from DB vessels
  const utilizationData = useMemo(() => {
    return vessels.map((v: any) => ({
      vessel: v.name.replace("MV ", ""),
      utilization: v.status === "deployed" || v.status === "available" ? 85 + Math.round(Math.random() * 12) : v.status === "maintenance" ? 20 + Math.round(Math.random() * 30) : 0,
    }));
  }, [vessels]);

  return (
    <IntranetLayout>
      {/* Welcome header with avatar */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold tracking-[3px] uppercase text-teal/60 mb-1">Velocity Offshore Intranet</p>
          <h1 className="font-display text-2xl md:text-3xl text-foreground tracking-tight">
            Welcome back, <span className="text-teal">{fullName || "Team Member"}</span>
          </h1>
          <p className="text-muted-foreground font-body mt-1 text-sm">
            Your central hub for operations, safety, and team collaboration.
          </p>
        </div>
        <Link to="/employee/settings" className="group flex-shrink-0">
          <div className="relative">
            <Avatar className="h-14 w-14 border-2 border-border group-hover:border-teal transition-colors">
              {avatarUrl ? <AvatarImage src={avatarUrl} alt={fullName || "Profile"} /> : null}
              <AvatarFallback className="text-lg font-bold bg-teal/10 text-teal">{initials}</AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center">
              <Settings className="w-3 h-3 text-muted-foreground" />
            </div>
          </div>
        </Link>
      </div>

      {/* Fleet Status Cards — portal style */}
      {hasAccess("page_fleet") && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { label: "On Charter", count: statusCounts.on_charter, color: "#28A745", icon: Ship },
            { label: "Standby", count: statusCounts.standby, color: "#FFC107", icon: Anchor },
            { label: "Maintenance", count: statusCounts.maintenance, color: "#DC3545", icon: AlertTriangle },
            { label: "Available", count: statusCounts.available, color: "#0066CC", icon: Gauge },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label} className="border-border">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + "18" }}>
                    <Icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-display text-foreground">{s.count}</p>
                    <p className="text-[11px] text-muted-foreground">{s.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* KPI Ticker Strip */}
      <Card className="mb-6 border-border">
        <CardContent className="p-3 flex flex-wrap items-center gap-4 md:gap-6 text-center">
          {[
            { label: "LTI-Free Days", value: PLATFORM_KPIS.ltiFreeDays.toString(), color: "#28A745", perm: "page_safety" },
            { label: "Fleet Availability", value: PLATFORM_KPIS.fleetAvailability + "%", color: "#0066CC", perm: "page_fleet" },
            { label: "On-Time Departure", value: PLATFORM_KPIS.onTimeDeparture + "%", color: "#0066CC", perm: "page_fleet" },
            { label: "Pending Questions", value: pendingQuestions.toString(), color: pendingQuestions > 0 ? "#FFC107" : "#28A745", perm: "page_questions" },
            { label: "Documents", value: docCount.toString(), color: "#10b981", perm: "page_documents" },
            { label: "Crew Onboard", value: onboardCrew.toString(), color: "#8b5cf6", perm: "page_crew_roster" },
            { label: "Active Contracts", value: activeContracts.toString(), color: "#06b6d4", perm: "page_contracts" },
            { label: "Avg Satisfaction", value: liveOverallScore + "/5", color: "#eab308", perm: "page_commercial" },
          ].filter(k => hasAccess(k.perm)).map((kpi, i) => (
            <div key={kpi.label} className={`flex-1 min-w-[80px] ${i > 0 ? "border-l border-border" : ""}`}>
              <p className="text-lg md:text-xl font-bold font-display" style={{ color: kpi.color }}>{kpi.value}</p>
              <p className="text-[9px] md:text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Main Content — Map + Feed */}
      {hasAccess("page_fleet") && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
          {/* Left — Map */}
          <div className="lg:col-span-3">
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-display text-foreground">Live Vessel Map</h2>
                  <div className="flex items-center gap-3 text-[10px]">
                    {Object.entries(VESSEL_STATUS_COLORS).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: val.bg }} />
                        <span className="text-muted-foreground">{val.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <VesselMap />
              </CardContent>
            </Card>
          </div>

          {/* Right — Certs + Voyages */}
          <div className="lg:col-span-2 space-y-4">
            {/* Certificate Expiry */}
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-display text-foreground">Certificate Expiry — Next 90 Days</h2>
                  <Link to="/employee/compliance" className="text-[10px] text-teal hover:underline flex items-center gap-1">
                    View All <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                {urgentCerts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">All certificates current ✓</p>
                ) : (
                  <div className="space-y-2">
                    {urgentCerts.slice(0, 5).map((cert) => (
                      <div key={cert.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          cert.status === "expired" ? "bg-destructive" : cert.status === "expiring" ? "bg-amber-400" : "bg-teal"
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{cert.name}</p>
                          <p className="text-[10px] text-muted-foreground">{cert.vessel}</p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          cert.status === "expired" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {cert.daysRemaining <= 0 ? `${Math.abs(cert.daysRemaining)}d overdue` : `${cert.daysRemaining}d`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Voyages */}
            <Card className="border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-display text-foreground">Recent Voyage Activity</h2>
                  <Link to="/employee/voyage-operations" className="text-[10px] text-teal hover:underline flex items-center gap-1">
                    View All <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="space-y-2">
                  {recentVoyages.map((voy) => (
                    <div key={voy.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        voy.status === "in_progress" ? "bg-blue-500" : "bg-teal"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">
                          {voy.voyageId} — {voy.vessel}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {voy.origin} → {voy.destination} · {voy.pax} pax
                        </p>
                      </div>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${
                        voy.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                      }`}>
                        {voy.status.replace("_", " ")}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Analytics Row — permission gated */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Satisfaction Trend */}
        {hasAccess("page_commercial") && (
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-display text-foreground">Satisfaction Trend</h2>
                <Link to="/employee/satisfaction" className="text-[10px] text-teal hover:underline">Details</Link>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                <span className="text-2xl font-bold font-display text-foreground">{liveOverallScore}</span>
                <span className="text-xs text-muted-foreground">/ 5</span>
              </div>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={liveTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[3, 5]} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Line type="monotone" dataKey="overall" stroke="#0066CC" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="crew" stroke="#28A745" strokeWidth={1} dot={false} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="punctuality" stroke="#FFC107" strokeWidth={1} dot={false} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Monthly Emissions */}
        {hasAccess("page_commercial") && (
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-display text-foreground">Monthly Emissions (MT CO₂)</h2>
                <Link to="/employee/esg-emissions" className="text-[10px] text-teal hover:underline">Details</Link>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={emissionsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Bar dataKey="pioneer" name="Pioneer" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="endeavour" name="Endeavour" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="benguela" name="Benguela" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Announcements */}
        {hasAccess("page_announcements") && recentAnnouncements.length > 0 && (
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-display text-foreground">Latest Announcements</h2>
                <Link to="/employee/announcements" className="text-[10px] text-teal hover:underline flex items-center gap-1">
                  View All <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="space-y-2">
                {recentAnnouncements.map((a: any) => (
                  <div key={a.id} className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        a.priority === "urgent" ? "bg-destructive" : a.priority === "important" ? "bg-amber-400" : "bg-teal"
                      }`} />
                      <div>
                        <p className="text-xs font-medium text-foreground">{a.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{a.content}</p>
                        <p className="text-[9px] text-muted-foreground/60 mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* SMS Compliance — permission gated */}
      {hasAccess("page_compliance") && complianceData.length > 0 && (
        <Card className="mb-6 border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg text-foreground flex items-center gap-2">
                <Scale className="w-5 h-5 text-teal" />
                SMS Document Compliance
              </h2>
              <Link to="/employee/documents" className="text-teal text-sm font-body hover:underline flex items-center gap-1">
                View Documents <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <p className="text-xs text-muted-foreground font-body mb-4">
              Read confirmation progress for SMS review documents ({complianceData[0]?.totalDocs || 0} total documents)
            </p>
            <div className="space-y-4">
              {complianceData.map((person) => (
                <div key={person.name}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-foreground font-body">{person.name}</span>
                    <span className="text-xs text-muted-foreground font-body">
                      {person.readCount}/{person.totalDocs} — <span className={`font-semibold ${person.pct === 100 ? "text-teal" : person.pct >= 50 ? "text-amber-500" : "text-destructive"}`}>{person.pct}%</span>
                    </span>
                  </div>
                  <Progress value={person.pct} className="h-2.5 bg-muted" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Client Questions */}
      {hasAccess("page_questions") && recentQuestions.length > 0 && (
        <Card className="mb-6 border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg text-foreground flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Client Questions
              </h2>
              <Link to="/employee/questions" className="text-teal text-sm font-body hover:underline">Answer All</Link>
            </div>
            <div className="space-y-2">
              {recentQuestions.map((q: any) => (
                <Link key={q.id} to="/employee/questions" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                  <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground font-body truncate">{q.title}</p>
                    <p className="text-xs text-muted-foreground font-body">
                      {(q.companies as { name: string } | null)?.name ?? "Unknown"} · {new Date(q.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/30" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Access — All granted modules */}
      <Card className="border-border">
        <CardContent className="p-6">
          <h2 className="font-display text-lg text-foreground mb-4">Your Modules</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-2">
            {accessibleModules.map((mod) => {
              const Icon = mod.icon;
              return (
                <Link
                  key={mod.href + mod.label}
                  to={mod.href}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: mod.color + "18" }}>
                    <Icon className="w-4 h-4" style={{ color: mod.color }} />
                  </div>
                  <span className="text-xs font-medium text-foreground group-hover:text-teal transition-colors truncate">{mod.label}</span>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </IntranetLayout>
  );
};

export default EmployeeDashboard;
